import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Clock } from 'lucide-react';
import api from '../../utils/api';
import { CourierShell, CourierStepper, C } from '../../components/courier/CourierUI';

export default function CourierTrack() {
  const { awb: awbParam } = useParams();
  const nav = useNavigate();
  const [awb, setAwb] = useState(awbParam || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (code) => {
    if (!code) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await api.get(`/courier/track/${code.trim().toUpperCase()}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Tracking number not found.' : 'Could not load tracking.');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (awbParam) load(awbParam); }, [awbParam]); // eslint-disable-line

  return (
    <CourierShell>
      <h1 className="text-3xl font-black mb-4">Track a Shipment</h1>
      <div className="flex gap-2 max-w-xl mb-8">
        <input value={awb} onChange={(e) => setAwb(e.target.value.toUpperCase())} placeholder="TKS-1234-5678" data-testid="track-input"
          className="flex-1 px-4 py-3 rounded-xl border border-black/15 font-mono focus:outline-none focus:ring-2 focus:ring-black"
          onKeyDown={(e) => e.key === 'Enter' && nav(`/courier/track/${awb.trim()}`)} />
        <button onClick={() => nav(`/courier/track/${awb.trim()}`)} data-testid="track-btn"
          className="px-5 rounded-xl font-black text-black flex items-center gap-1" style={{ background: C.yellow }}>
          <Search className="h-4 w-4" /> Track
        </button>
      </div>

      {loading && <p className="text-black/50" data-testid="track-loading">Searching…</p>}
      {error && <div className="p-4 rounded-xl bg-red-50 text-red-700 font-bold max-w-xl" data-testid="track-error">{error}</div>}

      {data && (
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-black/5 max-w-3xl" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-black/40">Waybill</p>
              <p className="font-mono text-xl font-black" data-testid="track-awb">{data.awb}</p>
            </div>
            <div className="text-right">
              <span className="px-3 py-1 rounded-full text-xs font-black" style={{ background: C.yellow }} data-testid="track-status">
                {(data.status || '').replace(/_/g, ' ').toUpperCase()}
              </span>
              {data.eta && <p className="text-xs text-black/50 mt-1 flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> ETA {data.eta}</p>}
            </div>
          </div>

          <CourierStepper stages={data.stages} stageIndex={data.stageIndex} status={data.status} />

          <div className="flex justify-between text-sm mt-6 text-black/60">
            <span>{data.origin?.zoneName}</span>
            <span>→ {data.destination?.zoneName}</span>
          </div>

          <div className="mt-8">
            <p className="font-black mb-3">Scan history</p>
            {data.events?.length ? (
              <ol className="space-y-3" data-testid="track-events">
                {data.events.map((e, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="h-2.5 w-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: i === 0 ? C.red : 'rgba(0,0,0,0.25)' }} />
                    <div>
                      <p className="font-bold text-sm">{(e.type || '').replace(/_/g, ' ')}</p>
                      <p className="text-xs text-black/50">{e.hub ? `${e.hub} · ` : ''}{e.note || ''}</p>
                      <p className="text-[11px] text-black/40">{new Date(e.at).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-black/40">No scans yet. Your shipment is being prepared.</p>}
          </div>
        </div>
      )}
    </CourierShell>
  );
}
